import type { TFlightSearchFormData } from "@/feature/flight/search/types/search";

export type AiChatResponseType =
  | "FLIGHT_SEARCH_RESULT"
  | "HOTEL_SEARCH_RESULT"
  | "NEED_MORE_INFO"
  | "GENERAL"
  | "ERROR";

type AiDynamicObject = Record<string, unknown>;

export type AiChatRequestBody = {
  sessionId: string;
  message: string;
};

export type AiChatMeta = {
  cached?: boolean;
  source?: "redis" | "api" | string;
  intent?: string;
};

export type AiChatSearchIds = {
  flight_search_id?: string | null;
  hotel_search_id?: string | null;
};

export type AiRequiredField = {
  key: string;
  label: string;
  source?: string;
  suggestions?: AiSuggestedQuery[];
};

export type AiSuggestedQuery = {
  id: string;
  label: string;
  query: string;
  category?: string;
  fieldKey?: string;
};

export type AiChatInternalMeta = {
  intent?: string;
  search_id?: AiChatSearchIds;
  waiting_for_user?: boolean;
  suggested_filters?: string[];
  suggested_queries?: AiSuggestedQuery[];
};

export type AiFlightSegment = AiDynamicObject & {
  from?: string;
  to?: string;
  dep_time?: string;
  arr_time?: string;
  dep_date?: string;
  arr_date?: string;
};

export type AiFlight = AiDynamicObject & {
  flight_id?: string;
  airline_code?: string;
  airline_name?: string;
  airline_logo?: string;
  price?: number;
  refundable?: boolean;
  duration?: string;
  total_stops?: number;
  segments?: AiFlightSegment[];
};

export type AiFlightSearchResultData = AiDynamicObject & {
  search_id?: string;
  flights?: AiFlight[];
  has_more?: boolean;
  shown?: number;
  total_available?: number;
};

export type AiHotelImage = AiDynamicObject & {
  id?: number;
  image_url?: string;
  image_caption?: string;
};

export type AiHotelSearchRoom = {
  adults?: number;
  children?: number;
  infants?: number;
  children_ages?: number[];
};

export type AiHotelSearchLocation = {
  id?: number;
  name?: string;
  code?: number;
  star_category?: number;
  type?: "Hotel" | "City" | string;
  city_code?: number;
  country_code?: string;
  country_name?: string;
  rank?: number;
};

export type AiHotelSearchDate = {
  from?: string;
  to?: string;
};

export type AiHotelSearchParams = {
  location?: AiHotelSearchLocation;
  date?: AiHotelSearchDate;
  rooms?: AiHotelSearchRoom[];
};

export type AiHotelNavigation = {
  hotel_details_query?: {
    parent_search_id?: string;
    search_id?: string;
    hcode?: string;
  };
  hotel_search_params?: AiHotelSearchParams;
};

export type AiHotel = AiDynamicObject & {
  hotel_code?: string;
  search_id?: string;
  parent_search_id?: string;
  name?: string;
  category?: number;
  currency?: string;
  price?: number;
  tax?: number;
  total_price?: number;
  refundable?: boolean;
  images?: {
    main_image?: string;
    additional_images?: AiHotelImage[];
  };
  main_image?: string;
  additional_images?: AiHotelImage[];
  image_url?: string;
  thumbnail?: string;
  address?: string;
  country?: string;
  facilities?: string;
  geolocation?: {
    latitude?: number;
    longitude?: number;
  };
  room_info?: {
    room_type?: string;
    boarding_details?: string[];
    group_code?: string;
    rate_key?: string;
  };
  navigation?: AiHotelNavigation;
};

export type AiHotelSearchResultData = AiDynamicObject & {
  search_id?: string;
  parent_search_id?: string;
  no_of_nights?: number;
  checkin?: string;
  checkout?: string;
  rooms?: AiHotelSearchRoom[];
  location?: AiHotelSearchLocation;
  search_params?: AiHotelSearchParams;
  hotels?: AiHotel[];
  has_more?: boolean;
  shown?: number;
  total_available?: number;
};

export type AiSeeMoreFlightsActionPayload = AiDynamicObject & {
  shown?: number;
  total_available?: number;
  search_params?: TFlightSearchFormData;
};

type AiChatActionBase<
  TType extends string = string,
  TPayload extends AiDynamicObject = AiDynamicObject,
> = {
  type: TType;
  label?: string;
  payload?: TPayload;
};

export type AiSeeMoreFlightsAction = AiChatActionBase<
  "SEE_MORE_FLIGHTS",
  AiSeeMoreFlightsActionPayload
>;

export type AiSeeMoreHotelsActionPayload = AiDynamicObject & {
  parent_search_id?: string;
  shown?: number;
  total_available?: number;
  search_params?: AiHotelSearchParams;
};

export type AiSeeMoreHotelsAction = AiChatActionBase<
  "SEE_MORE_HOTELS",
  AiSeeMoreHotelsActionPayload
>;

export type AiSuggestedFiltersAction = AiChatActionBase<"SUGGESTED_FILTERS"> & {
  suggestions?: string[];
  items?: AiSuggestedQuery[];
};

export type AiClarificationSuggestionsAction =
  AiChatActionBase<"CLARIFICATION_SUGGESTIONS"> & {
    items?: AiSuggestedQuery[];
  };

export type AiChatAction = (
  | AiSeeMoreFlightsAction
  | AiSeeMoreHotelsAction
  | AiSuggestedFiltersAction
  | AiClarificationSuggestionsAction
  | AiChatActionBase
) &
  AiDynamicObject;

export type AiChatActionType = AiChatAction["type"];

export type AiNeedMoreInfoData = AiDynamicObject & {
  missing?: string[];
  clarification_mode?: string;
  required_fields?: AiRequiredField[];
  highlight_phrases?: string[];
  suggested_queries?: AiSuggestedQuery[];
};

type AiChatPayloadBase<
  TType extends AiChatResponseType,
  TData extends AiDynamicObject | undefined,
> = {
  success: boolean;
  type: TType;
  message: string;
  data?: TData;
  actions?: AiChatAction[];
  meta?: AiChatMeta;
  _meta?: AiChatInternalMeta;
};

export type AiFlightSearchPayload = AiChatPayloadBase<
  "FLIGHT_SEARCH_RESULT",
  AiFlightSearchResultData
>;

export type AiHotelSearchPayload = AiChatPayloadBase<
  "HOTEL_SEARCH_RESULT",
  AiHotelSearchResultData
>;

export type AiNeedMoreInfoPayload = AiChatPayloadBase<
  "NEED_MORE_INFO",
  AiNeedMoreInfoData
>;

export type AiGeneralPayload = AiChatPayloadBase<"GENERAL", AiDynamicObject>;

export type AiErrorPayload = AiChatPayloadBase<"ERROR", AiDynamicObject>;

export type AiChatPayload =
  | AiFlightSearchPayload
  | AiHotelSearchPayload
  | AiNeedMoreInfoPayload
  | AiGeneralPayload
  | AiErrorPayload;

export type AiChatPayloadData = AiChatPayload["data"];
export type AiChatPayloadDataByType<TType extends AiChatResponseType> = Extract<
  AiChatPayload,
  { type: TType }
>["data"];

export type AiChatApiEnvelope = {
  success: boolean;
  data?: AiChatPayload;
  message?: string;
};

export function isSeeMoreFlightsAction(
  action: AiChatAction,
): action is AiSeeMoreFlightsAction {
  return action.type === "SEE_MORE_FLIGHTS";
}

export function isSeeMoreHotelsAction(
  action: AiChatAction,
): action is AiSeeMoreHotelsAction {
  return action.type === "SEE_MORE_HOTELS";
}

export function isFlightSearchData(
  data: AiChatPayloadData,
): data is AiFlightSearchResultData {
  return (
    !!data &&
    typeof data === "object" &&
    "flights" in data &&
    Array.isArray((data as AiFlightSearchResultData).flights)
  );
}

export function isHotelSearchData(
  data: AiChatPayloadData,
): data is AiHotelSearchResultData {
  return (
    !!data &&
    typeof data === "object" &&
    "hotels" in data &&
    Array.isArray((data as AiHotelSearchResultData).hotels)
  );
}

export function isNeedMoreInfoData(
  data: AiChatPayloadData,
): data is AiNeedMoreInfoData {
  return (
    !!data &&
    typeof data === "object" &&
    (("missing" in data &&
      Array.isArray((data as AiNeedMoreInfoData).missing)) ||
      ("required_fields" in data &&
        Array.isArray((data as AiNeedMoreInfoData).required_fields)) ||
      ("highlight_phrases" in data &&
        Array.isArray((data as AiNeedMoreInfoData).highlight_phrases)))
  );
}
